import React, { Suspense, useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { serverNow } from '../services/worldSocket';
import { cityTime } from '../city/cityTime';
import { listWorldObjects } from '../services/worldObjects';

// VenueSetList — tonight's performances, handwritten on a board by the door
// (2026-11-01).
//
// Khari: "Performance list in the Jahva house (should look like a handwritten
// list). This way I will be able to see performances there. (I haven't seen one
// yet)." They happen — two were booked at the Jahva House that day, at 11:23pm
// and 12:32am — but nothing in the room said so, and a spoken-word set late at
// night is easy to miss.
//
// THE LIST is the city's own timeline (the snapshot every client shares): each
// resident's stays with a set (`sh`) at this venue, from a couple of hours ago to
// the small hours. Done ones are struck through, the one on now is ringed, the
// rest wait. Nothing booked: it says so, rather than standing there blank.
//
// THE BOARD: an A-frame sandwich board with a sheet of paper taped to it, in
// marker (Permanent Marker, as RoomHostSign). Where it stands: one placed with
// ⚙ → Placeables → "Set list board" (room space), else the room's `setList`
// (rooms.js, room space at the room's fit × `k`, like the booth).
const FONT = `${import.meta.env.BASE_URL || '/'}fonts/PermanentMarker-Regular.ttf`;
const W = 0.34;          // ≈75 cm across (a person is ~0.75u)
const H = 0.48;          // ≈1.05 m of board
const LEAN = 0.2;        // each side's lean toward the other
// The feet stand this far apart, so the face and the legs MEET AT THE TOP (an A).
// 2026-11-01: they leaned away from each other and crossed at the feet — an
// upside-down V, "inverted" (Khari).
const SPREAD = H * Math.sin(LEAN);
const INK = '#16120e';
const DONE = '#6b6258';
const PAST_MS = 2 * 3600e3;      // done sets stay on the list this long
const AHEAD_MS = 16 * 3600e3;    // and it looks this far ahead
const MAX_LINES = 5;
// Each set is TWO rows: the time, and the full name under it (2026-11-01, Khari:
// "put the full name under the line, leaving only the time above it" — "6:40am
// Alma Sazon" on one row wrapped the surname under the strike).
const ENTRY_H = 0.062;
const NAME_W = W - 0.08;
/** A long name is written smaller rather than wrapped (Permanent Marker ≈0.62 em a letter). */
const nameSize = (name) => Math.min(0.027, NAME_W / Math.max(1, String(name).length * 0.62));

/** "9:40 PM" → "9:40pm": how a time is written by hand. */
const handTime = (ms) => cityTime(ms).replace(/\s*([AP])M$/i, (_, x) => `${x.toLowerCase()}m`);

/** Tonight's sets at this venue, from the shared timeline: [{ name, t0, t1 }]. */
export function setsAt(roomId, residents, now) {
  const out = [];
  for (const r of residents || []) {
    for (const s of r.segs || []) {
      if (!s.sh || s.p !== roomId) continue;
      if (s.t1 < now - PAST_MS || s.t0 > now + AHEAD_MS) continue;
      out.push({ name: r.name, t0: s.t0, t1: s.t1 });
    }
  }
  return out.sort((a, b) => a.t0 - b.t0);
}

function Line({ y, time, name, done, now: on }) {
  const fs = nameSize(name);
  const tilt = (y * 7) % 0.03 - 0.015;
  return (
    <group position={[0, y, 0]}>
      <Text font={FONT} fontSize={0.019} anchorX="left" anchorY="middle" position={[-W / 2 + 0.045, 0, 0]} color={done ? DONE : '#5a4f45'} rotation={[0, 0, tilt]} whiteSpace="nowrap">
        {time}
      </Text>
      <Text font={FONT} fontSize={fs} anchorX="left" anchorY="middle" position={[-W / 2 + 0.05, -0.026, 0]} color={done ? DONE : INK} rotation={[0, 0, tilt]} whiteSpace="nowrap">
        {name}
      </Text>
      {/* Crossed out, by hand: through the name. */}
      {done && (
        <mesh position={[-0.01, -0.025, 0.0005]} rotation={[0, 0, 0.03]}>
          <planeGeometry args={[W - 0.09, 0.004]} />
          <meshBasicMaterial color={DONE} />
        </mesh>
      )}
      {/* On now: an arrow in the margin. */}
      {on && (
        <Text font={FONT} fontSize={0.03} anchorX="right" anchorY="middle" position={[-W / 2 + 0.04, 0, 0]} color="#b3261e">
          {'→'}
        </Text>
      )}
    </group>
  );
}

export default function VenueSetList({ roomId, k = 1 }) {
  const room = ROOMS[roomId];
  const editing = useWorldStore((s) => s.adminTool) === 'placeables';
  const liveRows = useWorldStore((s) => s.worldObjects);
  const residents = useCityStore((s) => s.snap?.residents || null);
  const [placed, setPlaced] = useState(null);
  const [now, setNow] = useState(() => serverNow());

  useEffect(() => {
    if (!roomId || editing) return undefined;
    let dead = false;
    listWorldObjects(roomId, 'set_list').then((rows) => { if (!dead) setPlaced(rows || []); }).catch(() => {});
    return () => { dead = true; };
  }, [roomId, editing]);
  // A set starting or ending changes the board without a new snapshot.
  useEffect(() => {
    const t = setInterval(() => setNow(serverNow()), 30000);
    return () => clearInterval(t);
  }, []);

  const rows = editing ? (liveRows || []).filter((o) => o.zone_id === roomId && o.kind === 'set_list') : placed;
  const own = rows?.[0] || null;
  const spec = own ? { pos: own.pos, rotY: own.rotY || 0 } : room?.setList ? { pos: room.setList.pos.map((v) => v * k), rotY: room.setList.rotY || 0 } : null;
  if (!spec) return null;

  const sets = setsAt(roomId, residents, now).slice(-MAX_LINES);
  const lines = sets.map((s) => ({
    time: handTime(s.t0),
    name: s.name,
    done: s.t1 <= now,
    on: s.t0 <= now && now < s.t1,
  }));

  return (
    <group position={spec.pos} rotation={[0, spec.rotY, 0]}>
      {/* The back legs: feet behind, leaning forward to the top. */}
      <group position={[0, 0, -SPREAD]} rotation={[LEAN, 0, 0]}>
        {[-1, 1].map((sx) => (
          <mesh key={`b${sx}`} position={[sx * (W / 2 - 0.012), H / 2, 0]}>
            <boxGeometry args={[0.016, H, 0.012]} />
            <meshStandardMaterial color="#2a1d14" roughness={0.9} />
          </mesh>
        ))}
      </group>
      {/* The face: feet in front, leaning back to the top (where the legs meet it). */}
      <group position={[0, 0, SPREAD]} rotation={[-LEAN, 0, 0]}>
        <mesh position={[0, H / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[W, H, 0.014]} />
          <meshStandardMaterial color="#3b2a1d" roughness={0.85} emissive="#1a120b" emissiveIntensity={0.3} />
        </mesh>
        {/* The paper, a little crooked, taped at the top. */}
        <group position={[0, H / 2 + 0.01, 0.0075]} rotation={[0, 0, 0.012]}>
          <mesh>
            <planeGeometry args={[W - 0.04, H - 0.05]} />
            <meshStandardMaterial color="#efe6d2" roughness={0.95} emissive="#5c5444" emissiveIntensity={0.55} />
          </mesh>
          {[-1, 1].map((sx) => (
            <mesh key={sx} position={[sx * (W / 2 - 0.045), (H - 0.05) / 2 - 0.006, 0.0005]} rotation={[0, 0, sx * 0.45]}>
              <planeGeometry args={[0.06, 0.018]} />
              <meshStandardMaterial color="#e8e2c8" roughness={0.6} transparent opacity={0.8} emissive="#4a4636" emissiveIntensity={0.3} />
            </mesh>
          ))}
          <Suspense fallback={null}>
            <group position={[0, 0, 0.001]}>
              <Text font={FONT} fontSize={0.048} anchorX="center" anchorY="middle" position={[0, (H - 0.05) / 2 - 0.05, 0]} rotation={[0, 0, -0.03]} color={INK}>
                TONIGHT
              </Text>
              {lines.length ? lines.map((l, i) => (
                <Line key={i} y={(H - 0.05) / 2 - 0.105 - i * ENTRY_H} time={l.time} name={l.name} done={l.done} now={l.on} />
              )) : (
                <Text font={FONT} fontSize={0.026} maxWidth={W - 0.08} anchorX="center" anchorY="middle" textAlign="center" position={[0, 0.02, 0]} color={DONE}>
                  {'nobody booked yet —\ncheck back later'}
                </Text>
              )}
            </group>
          </Suspense>
        </group>
      </group>
    </group>
  );
}
